import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Bell, Mail, Smartphone, Info } from "lucide-react";

const ROLE_LABELS = { admin: "Administrateurs", gestionnaire: "Gestionnaires" };

export default function NotificationRulesView() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [scopeType, setScopeType] = useState(isAdmin ? "global" : "company");
  const [companies, setCompanies] = useState([]);
  const [pools, setPools] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [entCompany, setEntCompany] = useState("");
  const [scopeId, setScopeId] = useState("");
  const [rows, setRows] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data || []);
      if (!isAdmin && (r.data || []).length) { setScopeId(r.data[0].id); setEntCompany(r.data[0].id); }
    }).catch(() => {});
    api.get("/pools").then((r) => setPools(r.data || [])).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, [isAdmin]);

  const needsEntity = scopeType !== "global";
  const load = useCallback(async () => {
    if (needsEntity && !scopeId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const p = new URLSearchParams({ scope_type: scopeType, ...(needsEntity ? { scope_id: scopeId } : {}) });
      const r = await api.get(`/alert-notification-rules?${p}`);
      setRows(r.data || []);
      try {
        const rc = await api.get(`/alert-notification-rules/recipient-candidates?${p}`);
        setCandidates(rc.data || []);
      } catch { setCandidates([]); }
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Accès non autorisé" : "Erreur de chargement");
      setRows([]);
    } finally { setLoading(false); }
  }, [scopeType, scopeId, needsEntity]);

  useEffect(() => { load(); }, [load]);

  const save = async (row, patch) => {
    const next = {
      notify_open: row.notify_open, notify_close: row.notify_close,
      channels: { in_app: !!row.channels?.in_app, email: !!row.channels?.email },
      recipient_roles: row.recipient_roles || [], ...patch,
    };
    try {
      const p = new URLSearchParams({ scope_type: scopeType, ...(needsEntity ? { scope_id: scopeId } : {}) });
      await api.put(`/alert-notification-rules/${row.type}?${p}`, next);
      toast.success("Règle enregistrée");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(e.response?.status === 403 ? "Non autorisé" : e.response?.status === 409 ? (d?.error || "Conflit de groupe") : "Erreur");
      load();
    }
  };

  const toggleRole = (row, role) => {
    const cur = new Set(row.recipient_roles || []);
    cur.has(role) ? cur.delete(role) : cur.add(role);
    save(row, { recipient_roles: [...cur] });
  };

  const toggleUser = (row, uid) => {
    const cur = new Set(row.recipient_user_ids || []);
    cur.has(uid) ? cur.delete(uid) : cur.add(uid);
    save(row, { recipient_user_ids: [...cur] });
  };

  const anyActive = (r) => r.notify_open || r.notify_close;

  return (
    <div data-testid="notification-rules-view">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="flex rounded-md border border-border overflow-hidden" data-testid="notif-scope-tabs">
          {(isAdmin ? [["global", "Toute la flotte"], ["company", "Société"], ["pool", "Groupe"], ["vehicle", "Véhicule"]]
                    : [["company", "Société"], ["pool", "Groupe"], ["vehicle", "Véhicule"]]).map(([v, l]) => (
            <button key={v} onClick={() => { setScopeType(v); setScopeId(v === "company" ? entCompany : ""); }} data-testid={`notif-scope-${v}`}
              className={`px-3 py-2 text-xs font-semibold ${scopeType === v ? "bg-[#17A2B8] text-white" : "bg-card text-muted-foreground hover:bg-black/5"}`}>{l}</button>
          ))}
        </div>
        {scopeType === "company" && (
          <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="notif-company" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs">
            <option value="">— Choisir une société —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        {(scopeType === "pool" || scopeType === "vehicle") && (
          <>
            <select value={entCompany} onChange={(e) => { setEntCompany(e.target.value); setScopeId(""); }} data-testid="notif-ent-company" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs">
              <option value="">— Société —</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {scopeType === "pool" ? (
              <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="notif-pool" disabled={!entCompany} className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs disabled:opacity-50">
                <option value="">— Choisir un groupe —</option>
                {pools.filter((p) => !entCompany || p.company_id === entCompany).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            ) : (
              <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} data-testid="notif-vehicle" disabled={!entCompany} className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs disabled:opacity-50">
                <option value="">— Choisir un véhicule —</option>
                {vehicles.filter((v) => !entCompany || v.company_id === entCompany).map((v) => <option key={v.id} value={v.id}>{v.name}{v.plate ? ` · ${v.plate}` : ""}</option>)}
              </select>
            )}
          </>
        )}
        <span className="text-xs text-muted-foreground flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Priorité : Véhicule &gt; Groupe &gt; Société &gt; Global. Sans règle : notifications désactivées.</span>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Chargement…</div>
      ) : (needsEntity && !scopeId) ? (
        <div className="py-12 text-center text-sm text-muted-foreground" data-testid="notif-pick-company">Choisissez une portée ({scopeType === "company" ? "société" : scopeType === "pool" ? "groupe" : "véhicule"}) pour configurer ses notifications.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <div key={row.type} className="border border-border rounded-lg bg-card p-4" data-testid={`notif-rule-${row.type}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="font-semibold">{row.label}</div>
                {!anyActive(row) && <span className="text-[11px] text-muted-foreground" data-testid={`notif-off-${row.type}`}>Notifications désactivées</span>}
              </div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={row.notify_open} data-testid={`notify-open-${row.type}`} onChange={(e) => save(row, { notify_open: e.target.checked })} /> Notifier à l'ouverture
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={row.notify_close} data-testid={`notify-close-${row.type}`} onChange={(e) => save(row, { notify_close: e.target.checked })} /> Notifier au rétablissement
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!row.channels?.in_app} data-testid={`channel-inapp-${row.type}`} onChange={(e) => save(row, { channels: { ...row.channels, in_app: e.target.checked } })} />
                  <Bell className="w-4 h-4 text-[#17A2B8]" /> In-app
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!row.channels?.email} data-testid={`channel-email-${row.type}`} onChange={(e) => save(row, { channels: { ...row.channels, email: e.target.checked } })} />
                  <Mail className="w-4 h-4 text-[#F59E0B]" /> Email
                </label>
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-not-allowed" title="Aucun fournisseur SMS configuré">
                  <input type="checkbox" disabled data-testid={`channel-sms-${row.type}`} />
                  <Smartphone className="w-4 h-4" /> SMS — Indisponible
                </label>
              </div>
              <div className="mt-3 pt-3 border-t border-border/50">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1.5">Destinataires</div>
                <div className="flex gap-4">
                  {["admin", "gestionnaire"].map((r) => (
                    <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={(row.recipient_roles || []).includes(r)} data-testid={`recipient-${r}-${row.type}`} onChange={() => toggleRole(row, r)} /> {ROLE_LABELS[r]}
                    </label>
                  ))}
                </div>
                <div className="mt-3">
                  <div className="text-[11px] text-muted-foreground mb-1.5">Comptes nommés (en plus des rôles — n'élargit aucun droit : chacun ne reçoit que dans son périmètre)</div>
                  {candidates.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground italic" data-testid={`notif-no-candidates-${row.type}`}>Aucun compte ciblable sur cette portée.</div>
                  ) : (
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5 max-h-40 overflow-y-auto" data-testid={`notif-accounts-${row.type}`}>
                      {candidates.map((c) => (
                        <label key={c.user_id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" checked={(row.recipient_user_ids || []).includes(c.user_id)}
                            data-testid={`recipient-user-${c.user_id}-${row.type}`}
                            onChange={() => toggleUser(row, c.user_id)} />
                          <span>{c.name || c.email || c.user_id}
                            <span className="text-[11px] text-muted-foreground"> · {c.role}{c.email ? "" : " · sans email"}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
